"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { navbarConfig } from "@/shared/navigation";

// Encapsulates the scroll-based show/hide navbar logic used by PrivateNavbar
export default function useNavbarVisibility(isMobileDevice: boolean) {
  const [isVisible, setIsVisible] = useState(true);
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  const lastScrollYRef = useRef(0);
  const lastScrollTimeRef = useRef(0);
  const velocityRef = useRef(0);
  const hideTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!mounted || !isMobileDevice || navbarConfig.NAVBAR_LOCK) {
      return;
    }

    lastScrollYRef.current = window.scrollY || 0;
    lastScrollTimeRef.current = Date.now();
    velocityRef.current = 0;

    const clearHideTimer = () => {
      if (hideTimeoutRef.current) {
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };

    const scheduleHide = () => {
      clearHideTimer();
      hideTimeoutRef.current = window.setTimeout(() => {
        setIsVisible(false);
        hideTimeoutRef.current = null;
      }, navbarConfig.NAVBAR_HIDE_DELAY_MS);
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const currentTime = Date.now();
      const lastY = lastScrollYRef.current;
      const lastTime = lastScrollTimeRef.current;

      const distance = currentScrollY - lastY;
      const timeDelta = currentTime - lastTime;
      const velocity = timeDelta > 0 ? Math.abs(distance) / timeDelta : 0;

      velocityRef.current =
        velocityRef.current * navbarConfig.VELOCITY_SMOOTHING +
        velocity * (1 - navbarConfig.VELOCITY_SMOOTHING);

      const isScrollingDown = currentScrollY > lastY;
      const isScrollingUp = currentScrollY < lastY;

      if (
        isScrollingUp ||
        currentScrollY < navbarConfig.NAVBAR_SCROLL_THRESHOLD_PX ||
        (isScrollingDown &&
          velocityRef.current < navbarConfig.NAVBAR_VELOCITY_THRESHOLD)
      ) {
        clearHideTimer();
        setIsVisible(true);
      } else if (
        isScrollingDown &&
        velocityRef.current >= navbarConfig.NAVBAR_VELOCITY_THRESHOLD &&
        currentScrollY > navbarConfig.NAVBAR_SCROLL_THRESHOLD_PX
      ) {
        scheduleHide();
      }

      lastScrollYRef.current = currentScrollY;
      lastScrollTimeRef.current = currentTime;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearHideTimer();
    };
  }, [mounted, isMobileDevice]);

  return {
    mounted,
    isVisible: navbarConfig.NAVBAR_LOCK || !isMobileDevice ? true : isVisible,
  };
}
